import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { customerOwnerOptions, hasActiveCustomerOwner } from "../lib/customerAccess.js";
import { getLocalCalendarDate, syncUntouchedDateDefault } from "../lib/date.js";
import { customerName, formatDate, formatDateTime, formatDuration, formatLatLng, formatTime, ownerLabel } from "../lib/format.js";
import { CustomerLink, displayCustomerName, EmployeeLink } from "../components/recordLinks.jsx";
import { AsyncActionButton, Metric, Panel, ResourceTable, SelectField, SimpleForm, TextField, Topbar } from "../components/ui.jsx";

export function FieldTrackingView({ businessDate, resources, customers, users, onCreate, onCloseVisit, openCustomer, openEmployee }) {
  const defaultPlanDate = businessDate || getLocalCalendarDate();
  const previousDefaultPlanDate = useRef(defaultPlanDate);
  const hasCustomers = customers.length > 0;
  const customerOptions = hasCustomers ? customerOwnerOptions(customers, users) : [{ value: "", label: "No customers available" }];
  const [planForm, setPlanForm] = useState({ customerId: customers[0]?.id ?? "", plannedDate: defaultPlanDate, purpose: "Sales visit" });
  const canPlanVisit = hasCustomers && hasActiveCustomerOwner(planForm.customerId, customers, users);
  const tracking = resources.fieldTracking ?? {};
  const dailyTelemetry = resources.employeeDailyTelemetry ?? { employees: [], usageEvents: [] };
  const visibleRoutes = dailyTelemetry.routes ?? [];
  const openVisits = dailyTelemetry.openVisits ?? [];
  const planFormRef = useRef(null);

  function confirmCloseVisit(row) {
    const repLabel = ownerLabel(users, row.userId);
    const customerLabel = displayCustomerName(customers, row.customerId, row.customerName);
    const confirmed = window.confirm(
      `Close active visit for ${repLabel} at ${customerLabel}? This will end the live halt and the employee app will recover on its next refresh.`,
    );
    if (!confirmed) return true;
    return onCloseVisit(row.id);
  }

  useEffect(() => {
    const fallbackCustomerId = customers[0]?.id || "";
    setPlanForm((current) =>
      customers.some((customer) => customer.id === current.customerId)
        ? current
        : { ...current, customerId: fallbackCustomerId },
    );
  }, [customers.map((customer) => customer.id).join("|")]);

  useEffect(() => {
    const previousDefaultDate = previousDefaultPlanDate.current;
    setPlanForm((current) => ({
      ...current,
      plannedDate: syncUntouchedDateDefault(current.plannedDate, previousDefaultDate, defaultPlanDate),
    }));
    previousDefaultPlanDate.current = defaultPlanDate;
  }, [defaultPlanDate]);

  return (
    <>
      <Topbar title="Field Tracking" subtitle="Same-day employee telemetry, carry-over active visits, travel routes, GPS/geofence validation, and exception monitoring." />
      <section className="metric-grid">
        <Metric label="Planned Visits" value={tracking.plannedVisits ?? 0} />
        <Metric label="Completed Plans" value={tracking.completedPlans ?? 0} />
        <Metric label="Pending Plans" value={tracking.pendingPlans ?? 0} />
        <Metric label="Needs Reassignment" value={tracking.reassignmentPlans ?? 0} />
        <Metric label="Canceled Plans" value={tracking.canceledPlans ?? 0} />
        <Metric label="Actual Visits" value={tracking.actualVisits ?? 0} />
        <Metric label="Geofence Validated" value={tracking.geofenceValidated ?? 0} />
        <Metric label="GPS Exceptions" value={tracking.gpsExceptions ?? 0} />
        <Metric label="Open Halts" value={tracking.openHalts ?? getOpenHaltCount(visibleRoutes)} />
        <Metric label="Carry-Over Halts" value={tracking.carryOverOpenHalts ?? 0} />
        <Metric label="Same-Day Reports" value={dailyTelemetry.employees?.filter((employee) => employee.hasSameDayData).length ?? 0} />
        <Metric label="Stale Reports" value={dailyTelemetry.employees?.filter((employee) => employee.staleSignal).length ?? 0} />
        <Metric label="Usage Events" value={dailyTelemetry.usageEvents?.length ?? 0} />
        <Metric label="Idle Alerts" value={dailyTelemetry.employees?.reduce((sum, employee) => sum + Number(employee.idleEvents || 0), 0) ?? 0} />
        <Metric label="Sync Review Alerts" value={dailyTelemetry.employees?.reduce((sum, employee) => sum + Number(employee.syncReviewAlerts || 0), 0) ?? 0} />
      </section>
      <section className="panel spaced">
        <h2>Employee Same-Day Telemetry</h2>
        <ResourceTable
          rows={dailyTelemetry.employees}
          columns={[
	            ["userId", "Employee", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
            ["status", "Status"],
	            ["openVisitCustomerId", "Open Visit", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.openVisitCustomerName} openCustomer={openCustomer} />],
            ["openVisitStartedAt", "Visit Started", formatDateTime],
            ["usageEvents", "Usage Events"],
            ["activeMinutes", "Active Time", formatDuration],
            ["idleEvents", "Idle Alerts"],
            ["latestIdleAt", "Last Idle Alert", formatDateTime],
            ["syncReviewAlerts", "Review Alerts"],
            ["latestSyncReviewAt", "Last Review Alert", formatDateTime],
            ["travelPoints", "Travel Points"],
            ["distanceKm", "Distance KM"],
            ["visits", "Visits"],
            ["latestLocationAt", "Last Location", formatDateTime],
            ["latestSignalAt", "Last Signal", formatDateTime],
            ["minutesSinceLastSignal", "Signal Gap", formatSignalGap],
          ]}
        />
      </section>
      <section className="panel spaced">
        <h2>Active Visits</h2>
        <ResourceTable
          rows={openVisits}
          columns={[
	            ["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
	            ["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />],
            ["date", "Visit Day", formatDate],
            ["checkInAt", "Check-In", formatDateTime],
            ["date", "Status", (value) => value === dailyTelemetry.date ? "In progress" : "Carry-over"],
          ]}
          action={(row) => (
            <div className="row-actions">
              <button className="mini-btn" type="button" disabled={!canPrefillPlan(row)} onClick={() => startPlanFromRow(row, "Follow-up after active visit")}>
                Plan Follow-up
              </button>
              <AsyncActionButton
                pendingLabel="Closing..."
                errorMessage="We could not close this visit. Please try again."
                onAction={() => confirmCloseVisit(row)}
              >
                Close Visit
              </AsyncActionButton>
            </div>
          )}
        />
      </section>
      <section className="content-grid">
        <div ref={planFormRef}>
          <Panel title="Plan Visit">
            {!hasCustomers ? <div className="notice">No customers are available in this reporting scope. Add or assign a prospect before planning a visit.</div> : null}
            {hasCustomers && !canPlanVisit ? <div className="notice">Selected customer is assigned to an inactive sales rep. Reassign the prospect before planning a visit.</div> : null}
            <SimpleForm disabled={!canPlanVisit} onSubmit={() => onCreate("/plans", planForm)}>
	              <SelectField
	                label="Customer"
	                value={planForm.customerId}
	                options={customerOptions}
	                disabled={!hasCustomers}
	                actionLabel="Add prospect"
	                onAction={openCustomer ? () => openCustomer(null) : undefined}
	                secondaryActionLabel="Open selected prospect"
	                secondaryActionDisabled={!planForm.customerId}
	                onSecondaryAction={openCustomer ? () => openCustomer(planForm.customerId) : undefined}
	                onChange={(customerId) => setPlanForm({ ...planForm, customerId })}
	              />
              <TextField label="Planned date" type="date" value={planForm.plannedDate} onChange={(plannedDate) => setPlanForm({ ...planForm, plannedDate })} />
              <TextField label="Purpose" value={planForm.purpose} onChange={(purpose) => setPlanForm({ ...planForm, purpose })} />
            </SimpleForm>
          </Panel>
        </div>
        <Panel title="GPS Exceptions">
	          <ResourceTable
	            rows={resources.gpsExceptions}
	            columns={[["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />], ["reason", "Reason"], ["status", "Status"], ["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />]]}
	            action={(row) => (
	              <button className="mini-btn" type="button" disabled={!canPrefillPlan(row)} onClick={() => startPlanFromRow(row, "GPS follow-up visit")}>
	                Plan Visit
	              </button>
	            )}
	          />
        </Panel>
      </section>
      <section className="panel spaced">
        <h2>Sales Rep Route Map</h2>
	        <RouteMap routes={visibleRoutes} users={users} customers={customers} openCustomer={openCustomer} openEmployee={openEmployee} />
      </section>
      <section className="panel spaced">
        <h2>Mobile App Usage Feed</h2>
        <ResourceTable
          rows={dailyTelemetry.usageEvents}
          columns={[
            ["timestamp", "Time", formatDateTime],
	            ["userId", "Employee", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
            ["eventType", "Event"],
            ["screen", "Screen"],
            ["action", "Action"],
            ["durationSeconds", "Duration", (value) => formatDuration(Number(value || 0) / 60)],
          ]}
        />
      </section>
      <section className="panel spaced">
        <h2>Planned vs Actual Visits</h2>
	        <ResourceTable
	          rows={resources.plans}
	          columns={[["customerId", "Customer", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.customerName} openCustomer={openCustomer} />], ["plannedDate", "Planned Date"], ["purpose", "Purpose"], ["status", "Status"], ["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />]]}
	          action={(row) => (
	            <button className="mini-btn" type="button" disabled={!canPrefillPlan(row)} onClick={() => startPlanFromRow(row)}>
	              Plan Again
	            </button>
	          )}
	        />
      </section>
    </>
  );

  function canPrefillPlan(row) {
    return Boolean(customers.some((customer) => customer.id === row.customerId));
  }

  function startPlanFromRow(row, purpose = row.purpose || "Sales visit") {
    if (!canPrefillPlan(row)) return;
    setPlanForm({
      customerId: row.customerId,
      plannedDate: defaultPlanDate,
      purpose,
    });
    window.setTimeout(() => {
      planFormRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      planFormRef.current?.querySelector("select, input")?.focus({ preventScroll: true });
    }, 0);
  }
}

function RouteMap({ routes, users, customers, openCustomer, openEmployee }) {
  const mapElement = useRef(null);
  const now = Date.now();
  const halts = getRouteHalts(routes, now);
  const routePointHistory = getRoutePoints(routes);

  useEffect(() => {
    if (!mapElement.current) return undefined;
    const map = L.map(mapElement.current, { scrollWheelZoom: false, attributionControl: false });
    createLocalRouteGridLayer().addTo(map);

    const allPoints = routes.flatMap((route) => route.points || []).filter((point) => point.latitude && point.longitude);
    const bounds = [];
    routes.forEach((route, routeIndex) => {
      const routePoints = (route.points || []).filter((point) => point.latitude && point.longitude);
      const latLngs = routePoints.map((point) => [Number(point.latitude), Number(point.longitude)]);
      if (!latLngs.length) return;
      const color = routeIndex % 2 === 0 ? "#0e7c66" : "#d04f3b";
      L.polyline(latLngs, { color, weight: 5, opacity: 0.8 }).addTo(map);
      routePoints.forEach((point, pointIndex) => {
        const latLng = latLngs[pointIndex];
        bounds.push(latLng);
        const haltMinutes = getHaltDurationMinutes(point, now);
        const hasHalt = point.pointType === "visit" && point.haltStartAt;
        const haltLine = hasHalt
          ? `<br />Halt: ${formatHaltDuration(haltMinutes)} (${formatTime(point.haltStartAt || point.timestamp)}${point.haltEndAt ? ` - ${formatTime(point.haltEndAt)}` : " - In progress"})`
          : "";
        const pointType = point.pointType === "travel" ? "Travel point" : "Visit stop";
        L.circleMarker(latLng, { radius: 7, color, fillColor: color, fillOpacity: 0.85 })
          .addTo(map)
          .bindTooltip(`${pointType}: ${point.label || customerName(customers, point.customerId)}${hasHalt ? ` · halt ${formatHaltDuration(haltMinutes)}` : ""}`)
          .bindPopup(`${ownerLabel(users, route.userId)}<br />${pointType}<br />${point.label || customerName(customers, point.customerId)}<br />${formatLatLng(point)}${haltLine}`);
      });
    });

    if (bounds.length) {
      map.fitBounds(bounds, { padding: [32, 32], maxZoom: 13 });
    } else if (allPoints.length) {
      map.setView([allPoints[0].latitude, allPoints[0].longitude], 12);
    } else {
      map.setView([28.6139, 77.209], 10);
    }

    setTimeout(() => map.invalidateSize(), 0);
    return () => map.remove();
  }, [routes, users, customers]);

  return (
    <div>
      <div className="route-map" ref={mapElement} data-testid="route-map" />
      <div className="list compact-list">
        {routes.map((route) => (
          <div className="list-item" key={route.id}>
            <strong>{ownerLabel(users, route.userId)} route - {formatDate(route.date)}</strong>
            <span>{route.points?.length ?? 0} points · {Number(route.distanceKm || 0).toFixed(1)} km · {route.status}</span>
          </div>
        ))}
      </div>
      <div className="spaced">
        <h3>Route Halts / Stoppages</h3>
        <ResourceTable
          rows={halts}
          columns={[
	            ["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
	            ["customerId", "Stop", (value, row) => <CustomerLink customerId={value} customers={customers} fallbackName={row.label || customerName(customers, value)} openCustomer={openCustomer} />],
            ["haltStartAt", "Halt Time", formatDateTime],
            ["haltEndAt", "End Time", formatTime],
            ["haltStatus", "Status"],
            ["haltDurationMinutes", "Duration", (value) => formatHaltDuration(value)],
          ]}
        />
      </div>
      <div className="spaced">
        <h3>Route Point History</h3>
        <ResourceTable
          rows={routePointHistory}
          columns={[
	            ["userId", "Rep", (value) => <EmployeeLink userId={value} users={users} openEmployee={openEmployee} />],
            ["pointType", "Type", (value) => value === "travel" ? "Travel" : "Visit"],
            ["timestamp", "Time", formatDateTime],
	            ["label", "Location / Label", (value, row) => <CustomerLink customerId={row.customerId} customers={customers} fallbackName={value || customerName(customers, row.customerId)} openCustomer={openCustomer} />],
            ["coordinates", "Coordinates", (_value, row) => formatLatLng(row)],
            ["accuracy", "Accuracy", (value) => value ? `${Math.round(Number(value))} m` : "-"],
          ]}
        />
      </div>
    </div>
  );
}

function createLocalRouteGridLayer() {
  const LocalRouteGridLayer = L.GridLayer.extend({
    createTile(coords) {
      const tile = L.DomUtil.create("div", "route-map-tile");
      tile.setAttribute("aria-hidden", "true");
      tile.dataset.tile = `${coords.z}/${coords.x}/${coords.y}`;
      return tile;
    },
  });
  return new LocalRouteGridLayer({ tileSize: 256 });
}

function getRoutePoints(routes) {
  return routes
    .flatMap((route) =>
      (route.points || []).map((point, index) => ({
        ...point,
        id: point.id || `${route.id}-${point.activityId || index}`,
        routeId: route.id,
        userId: route.userId,
        teamId: route.teamId,
        pointType: point.pointType || (point.haltDurationMinutes ? "visit" : "travel"),
        coordinates: `${point.latitude},${point.longitude}`,
      })),
    )
    .sort((a, b) => String(b.timestamp || "").localeCompare(String(a.timestamp || "")));
}

function getRouteHalts(routes, now = Date.now()) {
  return routes.flatMap((route) =>
    (route.points || [])
      .filter((point) => point.pointType === "visit" && point.haltStartAt)
      .map((point) => ({
        ...point,
        id: `${route.id}-${point.activityId}`,
        routeId: route.id,
        userId: route.userId,
        teamId: route.teamId,
        haltStatus: point.haltEndAt ? "Closed" : "In progress",
        haltDurationMinutes: getHaltDurationMinutes(point, now),
      })),
  );
}

function getHaltDurationMinutes(point, now = Date.now()) {
  if (Number(point?.haltDurationMinutes || 0) > 0) return Number(point.haltDurationMinutes);
  if (!point?.haltStartAt) return 0;
  const endTime = point.haltEndAt ? new Date(point.haltEndAt).getTime() : now;
  const minutes = (endTime - new Date(point.haltStartAt).getTime()) / 60000;
  return Number.isFinite(minutes) && minutes > 0 ? Math.round(minutes) : 0;
}

function getOpenHaltCount(routes) {
  return routes.reduce(
    (sum, route) => sum + (route.points || []).filter((point) => point.pointType === "visit" && point.haltStartAt && !point.haltEndAt).length,
    0,
  );
}

function formatHaltDuration(value) {
  return Number(value || 0) > 0 ? formatDuration(value) : "0 min";
}

function formatSignalGap(value) {
  return value === "" || value === null || value === undefined ? "-" : `${value} min`;
}
